<template>
  <!--
    Approved email domains for an Email-domain restricted store. Mounted by
    StoreShopper only while that mode is chosen, so the mode radio is the one
    switch: the old "Restrict Email Domain" toggle here duplicated it, and
    its side effects made the mode impossible to set up (Bug Intake #313:
    getData() switched it off whenever the list was empty, which hid the
    domain input before a domain could be added, and wiped the saved list).
  -->
  <v-row align="center">
    <v-col cols="12" class="feature-drawer pa-4">
      <v-row>
        <v-col cols="12" md="5" class="pt-3">
          <div class="d-flex align-start">
            <v-form
              class="email-domain-form"
              ref="form"
              @submit.prevent="addEmailDomain"
            >
              <v-text-field
                label="Add Email Domain"
                prefix="@"
                type="text"
                v-model="newEmailDomain"
                :rules="[domainRule]"
                :disabled="disableFields"
                data-testid="ct-email-domain"
                persistent-hint
                hint="Shoppers must check out with an email on an approved domain. Exact match: acme.com does not admit mail.acme.com."
                placeholder="threadflow.com"
              />
            </v-form>
            <v-spacer />
            <v-btn
              @click="addEmailDomain"
              :disabled="disableFields || !normalizedNewDomain"
              data-testid="ct-email-domain-add"
              color="primary"
              class="ml-2"
              variant="flat"
            >
              Add Email Domain
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" md="1" />
        <v-col cols="12" md="6">
          <div
            class="md-body-no-spacing mb-3"
            style="font-weight: 600"
            v-if="emailDomains.length > 0"
          >
            Allowed Email Domains
          </div>
          <div
            v-for="(emailDomain, index) in emailDomains"
            :key="emailDomain"
            class="d-flex align-center"
            data-testid="ct-email-domain-row"
          >
            <p class="ml-2 subheading">
              <b>Domain: </b> {{ emailDomain }}
            </p>
            <v-spacer />
            <v-btn
              @click="removeEmailDomain(index)"
              :disabled="disableFields"
              variant="flat"
              icon
              aria-label="Remove domain"
              data-testid="ct-email-domain-remove"
            >
              <v-icon icon="delete" />
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { normalizeEmailDomain } from "@/utils/storeAccessRules";

export default {
  name: "StoreAccessControl",
  props: {
    campaign: Object,
    editMode: Boolean,
    disableFields: Boolean,
  },
  emits: ["campaign-changed", "domains-changed"],
  data() {
    return {
      emailDomains: [],
      newEmailDomain: "",
    };
  },
  computed: {
    normalizedNewDomain() {
      return normalizeEmailDomain(this.newEmailDomain);
    },
  },
  created() {
    // Loaded ONCE, when the section mounts. Reloading on every campaign
    // update would drop a domain added while a save was in flight; the
    // editor calls adoptSaved() when the saved list must win (a refused
    // save).
    this.adoptSaved(this.campaign?.email_domains);
  },
  methods: {
    domainRule(value) {
      if (!value || !String(value).trim()) return true;
      return (
        !!normalizeEmailDomain(value) ||
        "Enter a domain like acme.com (no @ before it, no spaces, no trailing dot)"
      );
    },

    addEmailDomain() {
      const domain = this.normalizedNewDomain;
      if (!domain) return;
      if (!this.emailDomains.includes(domain)) {
        this.emailDomains.push(domain);
      }
      this.newEmailDomain = "";
      this.$emit("domains-changed", [...this.emailDomains]);
      this.$emit("campaign-changed");
    },

    removeEmailDomain(index) {
      this.emailDomains.splice(index, 1);
      this.$emit("domains-changed", [...this.emailDomains]);
      this.$emit("campaign-changed");
    },

    /**
     * The approved-domain list as the owner has it on screen. Pure: reading
     * it never changes what is shown (the old version switched the section
     * off and wiped the list when it was empty -- #313). A half-typed domain
     * in the input is not part of it; only an added domain is.
     */
    getData() {
      return { email_domains: [...this.emailDomains] };
    },

    /** Replace the on-screen list with the saved one. */
    adoptSaved(domains) {
      this.emailDomains = Array.isArray(domains) ? [...domains] : [];
      this.$emit("domains-changed", [...this.emailDomains]);
    },
  },
};
</script>
